import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { BarChart, LineChart } from '@mantine/charts';
import {
  ActionIcon,
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  Checkbox,
  Collapse,
  Divider,
  Flex,
  Group,
  MultiSelect,
  Paper,
  Popover,
  ScrollArea,
  Select,
  SegmentedControl,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
  ThemeIcon,
  Tooltip,
} from '@mantine/core';
import {
  useDebouncedValue,
  useDisclosure,
  useLocalStorage,
} from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  ArrowUpDown,
  CalendarClock,
  ChevronDown,
  ChevronUp,
  Download,
  Filter,
  Globe2,
  MapPin,
  RefreshCw,
  Search,
  ShieldCheck,
  SlidersHorizontal,
  Users as UsersIcon,
} from 'lucide-react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  type PaginationState,
  getSortedRowModel,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { mapToObj, pipe } from 'remeda';
import { CSVLink } from 'react-csv';
import type { Users } from '../../types/database';
import { getSubscriptionExpiryInfo } from '../../utils/subscriptionExpiry';
import { useAdminUsersDirectoryQuery } from '../../hooks/queries/useAdminUsersDirectoryQuery';
import { TableLoadingSkeleton } from './TableLoadingSkeleton';
import { getLegacySourceBadge } from './legacySourceBadge';
import {
  ADMIN_USERS_CSV_HEADERS,
  createAdminUsersCsvData,
} from './adminUsersTable.csv';
import {
  applyAdminUsersFilters,
  DEFAULT_SUBSCRIPTION_FILTERS,
  enrichAdminUsers,
  formatAdminDate,
  getSubscriptionBreakdownData,
  getSubscriptionTimelineData,
  getEffectiveSubscriptionStatus,
  getSubscriptionStatusColor,
  getSubscriptionStatusLabel,
  getTopLocationsData,
  getTopSubscriptionPlansData,
  getUniqueOptions,
  getUserGrowthData,
  KNOWN_LEGACY_SOURCE_TAGS,
  summarizeSubscriptions,
  type AdminUsersFilterState,
  type EnrichedAdminUser,
  type UserGrowthRange,
} from './adminUsersTable.utils';

const PAGE_SIZES = ['10', '20', '50', '100'] as const;
const columnHelper = createColumnHelper<EnrichedAdminUser>();
const ADMIN_USERS_COLUMN_OPTIONS = [
  { id: 'id', label: 'ID' },
  { id: 'user', label: 'Utente' },
  { id: 'subscription', label: 'Subscription' },
  { id: 'subscriptionStart', label: 'Inizio' },
  { id: 'subscriptionEnd', label: 'Fine' },
  { id: 'subscriptionStatus', label: 'Stato' },
  { id: 'geolocation', label: 'Geolocalizzazione' },
] as const;

type AdminUsersColumnId = (typeof ADMIN_USERS_COLUMN_OPTIONS)[number]['id'];
type ColumnVisibilityState = Record<string, boolean>;

interface AdminUsersTableProps {
  onRowClick?: (user: Users) => void;
}

type StatCardProps = {
  title: string;
  value: number;
  description: string;
  color: string;
  icon: ReactNode;
};

type ChartCardProps = {
  title: string;
  description: string;
  children: ReactNode;
  actions?: ReactNode;
};

type SubscriptionIconGroupProps = {
  subscriptions: EnrichedAdminUser['normalizedSubscriptions'];
};

const initialFilters: Omit<AdminUsersFilterState, 'search'> = {
  subscriptionStatuses: DEFAULT_SUBSCRIPTION_FILTERS,
  subscriptionTypes: [],
  roles: [],
  accountTypes: [],
  geolocation: 'all',
  legacySources: [],
  assets: 'all',
};

const createColumnVisibilityState = (
  value?: Partial<Record<AdminUsersColumnId, boolean>>
): ColumnVisibilityState =>
  pipe(
    ADMIN_USERS_COLUMN_OPTIONS,
    mapToObj(option => [option.id, value?.[option.id] ?? true])
  );

const DEFAULT_COLUMN_VISIBILITY = createColumnVisibilityState();

const deserializeColumnVisibilityState = (
  value?: string
): ColumnVisibilityState => {
  if (!value) {
    return DEFAULT_COLUMN_VISIBILITY;
  }

  try {
    return createColumnVisibilityState(
      JSON.parse(value) as Partial<Record<AdminUsersColumnId, boolean>>
    );
  } catch {
    return DEFAULT_COLUMN_VISIBILITY;
  }
};

function StatCard({ title, value, description, color, icon }: StatCardProps) {
  return (
    <Paper withBorder radius='md' p='md'>
      <Group justify='space-between' align='flex-start' mb='sm'>
        <Stack gap={2}>
          <Text size='xs' tt='uppercase' fw={700} c='dimmed'>
            {title}
          </Text>
          <Text size='xl' fw={700}>
            {value}
          </Text>
        </Stack>
        <ThemeIcon variant='light' color={color} size='lg' radius='md'>
          {icon}
        </ThemeIcon>
      </Group>
      <Text size='sm' c='dimmed'>
        {description}
      </Text>
    </Paper>
  );
}

function ChartCard({ title, description, children, actions }: ChartCardProps) {
  return (
    <Card withBorder radius='md' p='lg'>
      <Group justify='space-between' align='flex-start' mb='md' wrap='wrap'>
        <Stack gap='xs'>
          <Text fw={600}>{title}</Text>
          <Text size='sm' c='dimmed'>
            {description}
          </Text>
        </Stack>
        {actions}
      </Group>
      {children}
    </Card>
  );
}

function EmptyChartState({ label }: { label: string }) {
  return (
    <Flex h={240} align='center' justify='center'>
      <Text size='sm' c='dimmed'>
        {label}
      </Text>
    </Flex>
  );
}

function SubscriptionIconGroup({ subscriptions }: SubscriptionIconGroupProps) {
  const activeSubscriptions = subscriptions.filter(
    subscription =>
      subscription.status === 'active' || subscription.status === 'trial'
  );
  const visibleSubscriptions =
    activeSubscriptions.length > 0
      ? activeSubscriptions
      : subscriptions.slice(0, 1);
  const primarySubscription = visibleSubscriptions[0];
  const additionalSubscriptions = visibleSubscriptions.slice(1);

  if (!primarySubscription) {
    return (
      <Badge variant='light' color='gray'>
        Nessuna
      </Badge>
    );
  }

  const primaryTooltipLabel = [
    primarySubscription.name,
    primarySubscription.description,
    `Stato: ${getSubscriptionStatusLabel(
      getEffectiveSubscriptionStatus(primarySubscription)
    )}`,
    `Inizio: ${formatAdminDate(primarySubscription.startDate)}`,
    `Fine: ${formatAdminDate(primarySubscription.endDate)}`,
  ]
    .filter(Boolean)
    .join('\n');

  const additionalTooltipLabel = additionalSubscriptions
    .map(subscription =>
      [
        subscription.name,
        `Stato: ${getSubscriptionStatusLabel(
          getEffectiveSubscriptionStatus(subscription)
        )}`,
        `Inizio: ${formatAdminDate(subscription.startDate)}`,
        `Fine: ${formatAdminDate(subscription.endDate)}`,
      ]
        .filter(Boolean)
        .join(' · ')
    )
    .join('\n');

  return (
    <Group gap='xs' wrap='nowrap'>
      <Tooltip
        label={
          <Text size='xs' style={{ whiteSpace: 'pre-line' }}>
            {primaryTooltipLabel}
          </Text>
        }
        multiline
        withArrow
      >
        <Avatar
          src={primarySubscription.image ?? undefined}
          radius='xl'
          size='sm'
          color={getSubscriptionStatusColor(
            getEffectiveSubscriptionStatus(primarySubscription)
          )}
        >
          {primarySubscription.name.slice(0, 2).toUpperCase()}
        </Avatar>
      </Tooltip>

      {additionalSubscriptions.length > 0 && (
        <Tooltip
          label={
            <Text size='xs' style={{ whiteSpace: 'pre-line' }}>
              {additionalTooltipLabel}
            </Text>
          }
          multiline
          withArrow
        >
          <Badge variant='light' color='blue'>
            +{additionalSubscriptions.length}
          </Badge>
        </Tooltip>
      )}
    </Group>
  );
}

type ColumnVisibilityControlProps = {
  columnVisibility: ColumnVisibilityState;
  onToggleColumn: (columnId: AdminUsersColumnId) => void;
  onResetColumns: () => void;
};

function ColumnVisibilityControl({
  columnVisibility,
  onToggleColumn,
  onResetColumns,
}: ColumnVisibilityControlProps) {
  const visibleColumnsCount = ADMIN_USERS_COLUMN_OPTIONS.filter(
    option => columnVisibility[option.id] !== false
  ).length;

  return (
    <Popover width={280} position='bottom-end' shadow='md'>
      <Popover.Target>
        <Button
          variant='light'
          color='gray'
          leftSection={<SlidersHorizontal size={16} />}
        >
          Personalizza tabella
        </Button>
      </Popover.Target>
      <Popover.Dropdown>
        <Stack gap='sm'>
          <Group justify='space-between' align='center'>
            <Stack gap={0}>
              <Text fw={600} size='sm'>
                Colonne visibili
              </Text>
              <Text size='xs' c='dimmed'>
                {visibleColumnsCount} di {ADMIN_USERS_COLUMN_OPTIONS.length}{' '}
                attive
              </Text>
            </Stack>
            <Button variant='subtle' size='compact-sm' onClick={onResetColumns}>
              Reset
            </Button>
          </Group>
          <Divider />
          <Stack gap='xs'>
            {ADMIN_USERS_COLUMN_OPTIONS.map(option => {
              const checked = columnVisibility[option.id] !== false;
              const isLastVisibleColumn = visibleColumnsCount === 1 && checked;

              return (
                <Checkbox
                  key={option.id}
                  label={option.label}
                  checked={checked}
                  disabled={isLastVisibleColumn}
                  onChange={() => onToggleColumn(option.id)}
                />
              );
            })}
          </Stack>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}

function formatAssetsSummary(user: EnrichedAdminUser): string | null {
  const parts: string[] = [];
  if (user.devicesCount > 0) {
    parts.push(
      `${user.devicesCount} device${user.devicesCount === 1 ? '' : 's'}`
    );
  }
  if (user.coursesCount > 0) {
    parts.push(
      `${user.coursesCount} cors${user.coursesCount === 1 ? 'o' : 'i'}`
    );
  }
  return parts.length > 0 ? parts.join(' · ') : null;
}

function UserOverviewCell({ user }: { user: EnrichedAdminUser }) {
  const assetsSummary = formatAssetsSummary(user);

  return (
    <Stack gap={2}>
      <Text fw={600} size='sm'>
        {user.displayName}
      </Text>
      <Text size='xs' c='dimmed'>
        {user.email}
      </Text>
      <Group gap='xs'>
        {user.role && (
          <Badge variant='light' color='indigo' size='sm'>
            {user.role}
          </Badge>
        )}
        {user.type && (
          <Badge variant='outline' color='gray' size='sm'>
            {user.type}
          </Badge>
        )}
        {user.legacy_source &&
          (() => {
            const badge = getLegacySourceBadge(user.legacy_source);
            if (!badge) return null;
            return (
              <Badge variant='filled' color={badge.color} size='sm'>
                {badge.label}
              </Badge>
            );
          })()}
      </Group>
      {assetsSummary && (
        <Text size='xs' c='dimmed'>
          {assetsSummary}
        </Text>
      )}
    </Stack>
  );
}

function GeolocationCell({ user }: { user: EnrichedAdminUser }) {
  if (!user.geolocationSummary) {
    return (
      <Text size='sm' c='dimmed'>
        Non disponibile
      </Text>
    );
  }

  return (
    <Stack gap={2}>
      <Group gap='xs'>
        <MapPin size={14} />
        <Text size='sm'>{user.geolocationSummary.label}</Text>
      </Group>
      {user.geolocationSummary.ipAddress && (
        <Text size='xs' c='dimmed'>
          IP: {user.geolocationSummary.ipAddress}
        </Text>
      )}
    </Stack>
  );
}

function SubscriptionTypeCell({ user }: { user: EnrichedAdminUser }) {
  return <SubscriptionIconGroup subscriptions={user.normalizedSubscriptions} />;
}

function SubscriptionEndCell({ user }: { user: EnrichedAdminUser }) {
  const endDate = user.primarySubscription?.endDate;
  const expiryInfo = getSubscriptionExpiryInfo(endDate);

  return (
    <Stack gap={2}>
      <Text size='sm'>{formatAdminDate(endDate)}</Text>
      {expiryInfo.daysUntilExpiry !== null &&
        expiryInfo.daysUntilExpiry > 0 && (
          <Text size='xs' c={expiryInfo.isExpiringSoon ? 'orange' : 'dimmed'}>
            {expiryInfo.daysUntilExpiry} giorni rimanenti
          </Text>
        )}
    </Stack>
  );
}

export function AdminUsersTable({ onRowClick }: AdminUsersTableProps) {
  const [pageSize, setPageSize] = useState(20);
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'id', desc: true },
  ]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });
  const [searchValue, setSearchValue] = useState('');
  const [subscriptionChartView, setSubscriptionChartView] = useState<
    'bar' | 'line'
  >('bar');
  const [growthRange, setGrowthRange] = useState<UserGrowthRange>('6m');
  const [debouncedSearch] = useDebouncedValue(searchValue, 250);
  const [filters, setFilters] = useState(initialFilters);
  const [filtersOpened, { toggle: toggleFilters }] = useDisclosure(false);
  const [isExportingCsv, setIsExportingCsv] = useState(false);
  const [columnVisibility, setColumnVisibility] =
    useLocalStorage<ColumnVisibilityState>({
      key: 'admin-users-table-column-visibility',
      defaultValue: DEFAULT_COLUMN_VISIBILITY,
      getInitialValueInEffect: false,
      serialize: value => JSON.stringify(createColumnVisibilityState(value)),
      deserialize: value => deserializeColumnVisibilityState(value),
    });
  const csvLinkRef = useRef<
    CSVLink & HTMLAnchorElement & { link?: HTMLAnchorElement }
  >(null);

  const { data, isLoading, isFetching, error, refetch } =
    useAdminUsersDirectoryQuery();

  const enrichedUsers = useMemo(
    () => enrichAdminUsers(data?.users ?? []),
    [data?.users]
  );

  const filterOptions = useMemo(() => {
    const presentLegacySources = getUniqueOptions(
      enrichedUsers.map(user => user.legacy_source)
    );
    const legacySources = getUniqueOptions([
      ...KNOWN_LEGACY_SOURCE_TAGS,
      ...presentLegacySources,
    ]);

    return {
      subscriptionTypes: getUniqueOptions(
        enrichedUsers.flatMap(user => user.subscriptionTypes)
      ),
      roles: getUniqueOptions(enrichedUsers.map(user => user.role)),
      accountTypes: getUniqueOptions(enrichedUsers.map(user => user.type)),
      legacySources,
    };
  }, [enrichedUsers]);

  const filteredUsers = useMemo(
    () =>
      applyAdminUsersFilters(enrichedUsers, {
        ...filters,
        search: debouncedSearch,
      }),
    [debouncedSearch, enrichedUsers, filters]
  );

  const summary = useMemo(
    () => summarizeSubscriptions(filteredUsers),
    [filteredUsers]
  );
  const subscriptionBreakdownData = useMemo(
    () => getSubscriptionBreakdownData(filteredUsers),
    [filteredUsers]
  );
  const subscriptionTimelineData = useMemo(
    () => getSubscriptionTimelineData(filteredUsers),
    [filteredUsers]
  );
  const topPlansData = useMemo(
    () => getTopSubscriptionPlansData(filteredUsers),
    [filteredUsers]
  );
  const topLocationsData = useMemo(
    () => getTopLocationsData(filteredUsers),
    [filteredUsers]
  );
  const growthData = useMemo(
    () => getUserGrowthData(filteredUsers, growthRange),
    [filteredUsers, growthRange]
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor('id', {
        header: 'ID',
        cell: info => info.getValue(),
      }),
      columnHelper.display({
        id: 'user',
        header: 'Utente',
        cell: ({ row }) => <UserOverviewCell user={row.original} />,
        sortingFn: (left, right) =>
          left.original.displayName.localeCompare(right.original.displayName),
      }),
      columnHelper.display({
        id: 'subscription',
        header: 'Subscription',
        cell: ({ row }) => <SubscriptionTypeCell user={row.original} />,
        sortingFn: (left, right) =>
          (left.original.primarySubscription?.name ?? '').localeCompare(
            right.original.primarySubscription?.name ?? ''
          ),
      }),
      columnHelper.display({
        id: 'subscriptionStart',
        header: 'Inizio',
        cell: ({ row }) =>
          formatAdminDate(row.original.primarySubscription?.startDate),
        sortingFn: (left, right) =>
          (left.original.primarySubscription?.startDate ?? '').localeCompare(
            right.original.primarySubscription?.startDate ?? ''
          ),
      }),
      columnHelper.display({
        id: 'subscriptionEnd',
        header: 'Fine',
        cell: ({ row }) => <SubscriptionEndCell user={row.original} />,
        sortingFn: (left, right) =>
          (left.original.primarySubscription?.endDate ?? '').localeCompare(
            right.original.primarySubscription?.endDate ?? ''
          ),
      }),
      columnHelper.display({
        id: 'subscriptionStatus',
        header: 'Stato',
        cell: ({ row }) => (
          <Badge
            color={getSubscriptionStatusColor(
              row.original.subscriptionVisibilityStatus
            )}
            variant='light'
          >
            {getSubscriptionStatusLabel(
              row.original.subscriptionVisibilityStatus
            )}
          </Badge>
        ),
        sortingFn: (left, right) =>
          left.original.subscriptionVisibilityStatus.localeCompare(
            right.original.subscriptionVisibilityStatus
          ),
      }),
      columnHelper.display({
        id: 'geolocation',
        header: 'Geolocalizzazione',
        cell: ({ row }) => <GeolocationCell user={row.original} />,
        sortingFn: (left, right) =>
          (left.original.geolocationSummary?.label ?? '').localeCompare(
            right.original.geolocationSummary?.label ?? ''
          ),
      }),
    ],
    []
  );

  const table = useReactTable({
    data: filteredUsers,
    columns,
    state: {
      columnVisibility,
      sorting,
      pagination,
    },
    onColumnVisibilityChange: setColumnVisibility,
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const sortedFilteredUsers = table
    .getSortedRowModel()
    .rows.map(row => row.original);

  const csvData = createAdminUsersCsvData(sortedFilteredUsers);

  useEffect(() => {
    setPagination(current => ({ ...current, pageIndex: 0 }));
  }, [
    debouncedSearch,
    filters.accountTypes,
    filters.assets,
    filters.geolocation,
    filters.legacySources,
    filters.roles,
    filters.subscriptionStatuses,
    filters.subscriptionTypes,
  ]);

  useEffect(() => {
    setPagination(current => ({ ...current, pageSize }));
  }, [pageSize]);

  if (isLoading) {
    return <TableLoadingSkeleton rows={8} columns={7} />;
  }

  if (error) {
    return (
      <Paper p='md' withBorder>
        <Text c='red' mb='md'>
          Errore nel caricamento utenti:{' '}
          {error instanceof Error ? error.message : 'Unknown error'}
        </Text>
        <Button
          onClick={() => void refetch()}
          leftSection={<RefreshCw size={16} />}
        >
          Riprova
        </Button>
      </Paper>
    );
  }

  const resetFilters = () => {
    setSearchValue('');
    setFilters(initialFilters);
  };

  const handleToggleColumn = (columnId: AdminUsersColumnId) => {
    const visibleColumnsCount = ADMIN_USERS_COLUMN_OPTIONS.filter(
      option => columnVisibility[option.id] !== false
    ).length;

    if (visibleColumnsCount === 1 && columnVisibility[columnId] !== false) {
      return;
    }

    setColumnVisibility((current: ColumnVisibilityState) =>
      createColumnVisibilityState({
        ...current,
        [columnId]: current[columnId] === false,
      })
    );
  };

  const handleResetColumns = () => {
    setColumnVisibility(DEFAULT_COLUMN_VISIBILITY);
  };

  const handleExportCsv = () => {
    if (csvData.length === 0) {
      notifications.show({
        color: 'yellow',
        title: 'Nessun dato da esportare',
        message:
          'Non ci sono utenti visibili nella tabella con i filtri attuali',
      });
      return;
    }

    setIsExportingCsv(true);
    csvLinkRef.current?.link?.click();

    window.setTimeout(() => {
      setIsExportingCsv(false);
      notifications.show({
        color: 'green',
        title: 'Export completato',
        message: `Esportati ${csvData.length} utenti con i filtri correnti`,
      });
    }, 300);
  };

  const totalRecords = filteredUsers.length;
  const startRecord =
    totalRecords === 0
      ? 0
      : table.getState().pagination.pageIndex *
          table.getState().pagination.pageSize +
        1;
  const endRecord = Math.min(
    (table.getState().pagination.pageIndex + 1) *
      table.getState().pagination.pageSize,
    totalRecords
  );

  return (
    <Stack gap='lg'>
      <SimpleGrid cols={{ base: 1, sm: 2, xl: 4 }}>
        <StatCard
          title='Utenti visibili'
          value={summary.totalUsers}
          description={`${summary.totalUsers} risultati sui ${enrichedUsers.length} utenti caricati`}
          color='indigo'
          icon={<UsersIcon size={18} />}
        />
        <StatCard
          title='Subscription attive'
          value={summary.activeUsers}
          description='Utenti con licenza attiva o trial'
          color='teal'
          icon={<ShieldCheck size={18} />}
        />
        <StatCard
          title='In scadenza'
          value={summary.expiringSoonUsers}
          description='Scadenze entro i prossimi due mesi'
          color='orange'
          icon={<CalendarClock size={18} />}
        />
        <StatCard
          title='Geolocalizzati'
          value={summary.geolocatedUsers}
          description='Utenti con almeno un riferimento geografico'
          color='blue'
          icon={<Globe2 size={18} />}
        />
      </SimpleGrid>

      <SimpleGrid cols={{ base: 1, xl: 2 }}>
        <ChartCard
          title='Distribuzione subscription'
          description='Stato attuale oppure totale cumulato delle subscription nel tempo'
          actions={
            <SegmentedControl
              size='xs'
              value={subscriptionChartView}
              onChange={value =>
                setSubscriptionChartView(value as 'bar' | 'line')
              }
              data={[
                { label: 'Colonne', value: 'bar' },
                { label: 'Timeline', value: 'line' },
              ]}
            />
          }
        >
          {subscriptionChartView === 'bar' ? (
            subscriptionBreakdownData.some(item => item.count > 0) ? (
              <BarChart
                h={240}
                data={subscriptionBreakdownData}
                dataKey='label'
                withLegend={false}
                series={[{ name: 'count', color: 'indigo.6' }]}
              />
            ) : (
              <EmptyChartState label='Nessun dato disponibile per i filtri selezionati' />
            )
          ) : subscriptionTimelineData.some(item => item.total > 0) ? (
            <LineChart
              h={240}
              data={subscriptionTimelineData}
              dataKey='month'
              curveType='linear'
              withDots
              series={[
                {
                  name: 'total',
                  label: 'Totale subscription',
                  color: 'indigo.6',
                },
              ]}
            />
          ) : (
            <EmptyChartState label='Nessun andamento disponibile nel periodo osservato' />
          )}
        </ChartCard>

        <ChartCard
          title='Crescita utenti'
          description='Nuovi utenti per periodo secondo Users.created_at'
          actions={
            <SegmentedControl
              size='xs'
              value={growthRange}
              onChange={value => setGrowthRange(value as UserGrowthRange)}
              data={[
                { label: 'Ultimi 30 gg', value: '30d' },
                { label: 'Ultimi 6 mesi', value: '6m' },
                { label: "Dall'inizio", value: 'all' },
              ]}
            />
          }
        >
          {growthData.some(item => item.users > 0) ? (
            <LineChart
              h={240}
              data={growthData}
              dataKey='month'
              withLegend={false}
              withDots
              curveType='linear'
              series={[{ name: 'users', color: 'teal.6' }]}
            />
          ) : (
            <EmptyChartState label='Nessuna crescita disponibile nel periodo osservato' />
          )}
        </ChartCard>

        <ChartCard
          title='Top subscription'
          description='Piani più presenti nella base filtrata'
        >
          {topPlansData.length > 0 ? (
            <BarChart
              h={240}
              data={topPlansData}
              dataKey='plan'
              withLegend={false}
              series={[{ name: 'count', color: 'blue.6' }]}
            />
          ) : (
            <EmptyChartState label='Nessuna subscription disponibile' />
          )}
        </ChartCard>

        <ChartCard
          title='Top geolocalizzazioni'
          description='Distribuzione delle località disponibili'
        >
          {topLocationsData.length > 0 ? (
            <BarChart
              h={240}
              data={topLocationsData}
              dataKey='location'
              withLegend={false}
              series={[{ name: 'count', color: 'cyan.6' }]}
            />
          ) : (
            <EmptyChartState label='Nessuna informazione geografica disponibile' />
          )}
        </ChartCard>
      </SimpleGrid>

      <Paper p='md' withBorder radius='md'>
        <Stack gap='md'>
          <Flex justify='space-between' align='flex-start' gap='md' wrap='wrap'>
            <Stack gap={4}>
              <Text fw={600}>Directory utenti</Text>
              <Text size='sm' c='dimmed'>
                Filtri avanzati, geolocalizzazione e stato subscription sempre
                sotto controllo
              </Text>
            </Stack>
            <Group gap='xs'>
              <CSVLink
                ref={csvLinkRef}
                data={csvData}
                headers={ADMIN_USERS_CSV_HEADERS}
                filename={`admin_users_${new Date().toISOString().split('T')[0]}.csv`}
                style={{ display: 'none' }}
              />
              <Button
                variant='light'
                color='green'
                onClick={handleExportCsv}
                leftSection={<Download size={16} />}
                loading={isExportingCsv}
              >
                Esporta CSV
              </Button>
              <ColumnVisibilityControl
                columnVisibility={columnVisibility}
                onToggleColumn={handleToggleColumn}
                onResetColumns={handleResetColumns}
              />
              <Button
                variant='light'
                onClick={toggleFilters}
                leftSection={<Filter size={16} />}
              >
                Filtri avanzati
              </Button>
              <ActionIcon
                variant='light'
                size='lg'
                loading={isFetching}
                onClick={() => void refetch()}
              >
                <RefreshCw size={18} />
              </ActionIcon>
            </Group>
          </Flex>

          <SimpleGrid cols={{ base: 1, md: 2, xl: 3 }}>
            <TextInput
              label='Ricerca utenti'
              placeholder='Cerca per nome, email, ruolo, location o subscription'
              value={searchValue}
              onChange={event => setSearchValue(event.currentTarget.value)}
              leftSection={<Search size={16} />}
            />
            <MultiSelect
              label='Stato subscription'
              data={[
                { value: 'active', label: 'Attive' },
                { value: 'expiringSoon', label: 'In scadenza' },
                { value: 'expired', label: 'Scadute' },
                { value: 'none', label: 'Senza sub' },
              ]}
              value={filters.subscriptionStatuses}
              onChange={value =>
                setFilters(current => ({
                  ...current,
                  subscriptionStatuses:
                    value.length > 0
                      ? (value as AdminUsersFilterState['subscriptionStatuses'])
                      : DEFAULT_SUBSCRIPTION_FILTERS,
                }))
              }
              clearable={false}
            />
            <Select
              label='Geolocalizzazione'
              value={filters.geolocation}
              onChange={value =>
                setFilters(current => ({
                  ...current,
                  geolocation:
                    (value as AdminUsersFilterState['geolocation']) ?? 'all',
                }))
              }
              data={[
                { value: 'all', label: 'Tutte' },
                { value: 'available', label: 'Disponibile' },
                { value: 'missing', label: 'Mancante' },
              ]}
            />
          </SimpleGrid>

          <Collapse in={filtersOpened}>
            <Stack gap='md'>
              <Divider />
              <SimpleGrid cols={{ base: 1, md: 2, xl: 3 }}>
                <MultiSelect
                  label='Tipo subscription'
                  placeholder='Tutti i piani'
                  data={filterOptions.subscriptionTypes.map(item => ({
                    value: item,
                    label: item,
                  }))}
                  value={filters.subscriptionTypes}
                  onChange={value =>
                    setFilters(current => ({
                      ...current,
                      subscriptionTypes: value,
                    }))
                  }
                  searchable
                  clearable
                />
                <MultiSelect
                  label='Ruolo'
                  placeholder='Tutti i ruoli'
                  data={filterOptions.roles.map(item => ({
                    value: item,
                    label: item,
                  }))}
                  value={filters.roles}
                  onChange={value =>
                    setFilters(current => ({
                      ...current,
                      roles: value,
                    }))
                  }
                  searchable
                  clearable
                />
                <MultiSelect
                  label='Tipo account'
                  placeholder='Tutti i tipi'
                  data={filterOptions.accountTypes.map(item => ({
                    value: item,
                    label: item,
                  }))}
                  value={filters.accountTypes}
                  onChange={value =>
                    setFilters(current => ({
                      ...current,
                      accountTypes: value,
                    }))
                  }
                  searchable
                  clearable
                />
                <MultiSelect
                  label='Provenienza'
                  placeholder='Tutti i tag legacy'
                  data={filterOptions.legacySources.map(source => {
                    const badge = getLegacySourceBadge(source);
                    return {
                      value: source,
                      label: badge?.label ?? source,
                    };
                  })}
                  value={filters.legacySources}
                  onChange={value =>
                    setFilters(current => ({
                      ...current,
                      legacySources: value,
                    }))
                  }
                  searchable
                  clearable
                />
                <Select
                  label='Dotazione'
                  value={filters.assets}
                  onChange={value =>
                    setFilters(current => ({
                      ...current,
                      assets:
                        (value as AdminUsersFilterState['assets']) ?? 'all',
                    }))
                  }
                  data={[
                    { value: 'all', label: 'Tutti' },
                    { value: 'devices', label: 'Con devices' },
                    { value: 'devicesOnly', label: 'Solo devices' },
                    { value: 'courses', label: 'Con corsi' },
                    { value: 'coursesOnly', label: 'Solo corsi' },
                    { value: 'both', label: 'Con corsi e devices' },
                  ]}
                />
              </SimpleGrid>
            </Stack>
          </Collapse>

          <Group justify='space-between' align='center' wrap='wrap'>
            <Text size='sm' c='dimmed'>
              Mostrando {startRecord} - {endRecord} di {totalRecords} utenti
            </Text>
            <Group gap='xs'>
              <Select
                value={String(pageSize)}
                onChange={value => setPageSize(Number(value ?? '20'))}
                data={PAGE_SIZES.map(size => ({
                  value: size,
                  label: `${size} / pagina`,
                }))}
                w={120}
              />
              <Button variant='subtle' color='gray' onClick={resetFilters}>
                Reset filtri
              </Button>
            </Group>
          </Group>

          <ScrollArea>
            <Table
              striped
              highlightOnHover
              withTableBorder
              withColumnBorders
              miw={1180}
            >
              <Table.Thead>
                {table.getHeaderGroups().map(headerGroup => (
                  <Table.Tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <Table.Th
                        key={header.id}
                        style={{
                          cursor: header.column.getCanSort()
                            ? 'pointer'
                            : 'default',
                          userSelect: 'none',
                        }}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        <Group gap='xs' wrap='nowrap'>
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                          {header.column.getCanSort() && (
                            <>
                              {header.column.getIsSorted() === 'asc' && (
                                <ChevronUp size={14} />
                              )}
                              {header.column.getIsSorted() === 'desc' && (
                                <ChevronDown size={14} />
                              )}
                              {!header.column.getIsSorted() && (
                                <ArrowUpDown
                                  size={14}
                                  style={{ opacity: 0.4 }}
                                />
                              )}
                            </>
                          )}
                        </Group>
                      </Table.Th>
                    ))}
                  </Table.Tr>
                ))}
              </Table.Thead>
              <Table.Tbody>
                {table.getRowModel().rows.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={columns.length}>
                      <Box py='xl'>
                        <Text ta='center' c='dimmed'>
                          Nessun utente trovato con i filtri selezionati
                        </Text>
                      </Box>
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  table.getRowModel().rows.map(row => (
                    <Table.Tr
                      key={row.id}
                      onClick={() => onRowClick?.(row.original)}
                      style={{
                        cursor: onRowClick ? 'pointer' : 'default',
                      }}
                    >
                      {row.getVisibleCells().map(cell => (
                        <Table.Td
                          key={cell.id}
                          style={{ verticalAlign: 'top' }}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </Table.Td>
                      ))}
                    </Table.Tr>
                  ))
                )}
              </Table.Tbody>
            </Table>
          </ScrollArea>

          <Flex justify='space-between' align='center' wrap='wrap' gap='md'>
            <Text size='sm' c='dimmed'>
              Pagina {table.getState().pagination.pageIndex + 1} di{' '}
              {Math.max(table.getPageCount(), 1)}
            </Text>
            <Group gap='xs'>
              <Button
                variant='light'
                disabled={!table.getCanPreviousPage()}
                onClick={() => table.previousPage()}
              >
                Precedente
              </Button>
              <Button
                variant='light'
                disabled={!table.getCanNextPage()}
                onClick={() => table.nextPage()}
              >
                Successiva
              </Button>
            </Group>
          </Flex>
        </Stack>
      </Paper>
    </Stack>
  );
}
